import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import type { Notification, NotificationType } from '@court-sync/shared';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils';
import { academyService } from '@/services/academy';
import { clubService } from '@/services/club';
import { CalendarCheck, CalendarX, ReceiptText, UserX, Inbox, Mail, Link2, LandPlot, Trophy } from 'lucide-react';

function activityVisuals(
  type: NotificationType,
  entityType?: string
): {
  Icon: typeof CalendarCheck;
  iconClass: string;
} {
  if (entityType === 'court_hire_booking') {
    return { Icon: LandPlot, iconClass: 'text-teal-600 dark:text-teal-400' };
  }

  if (entityType === 'play_event') {
    return { Icon: Trophy, iconClass: 'text-violet-600 dark:text-violet-400' };
  }

  switch (type) {
    case 'booking.created':
    case 'booking.confirmed':
      return { Icon: CalendarCheck, iconClass: 'text-emerald-600 dark:text-emerald-400' };
    case 'booking.cancelled':
      return { Icon: CalendarX, iconClass: 'text-red-600 dark:text-red-400' };
    case 'booking.term-ended-cleanup-reminder':
      return { Icon: CalendarCheck, iconClass: 'text-amber-600 dark:text-amber-400' };
    case 'makeup_credit.absence_reported':
      return { Icon: UserX, iconClass: 'text-amber-600 dark:text-amber-400' };
    case 'lesson.coach_replaced':
      return { Icon: CalendarCheck, iconClass: 'text-sky-600 dark:text-sky-400' };
    case 'refund.requested':
      return { Icon: ReceiptText, iconClass: 'text-amber-600 dark:text-amber-400' };
    case 'eoi.submitted':
      return { Icon: Mail, iconClass: 'text-indigo-600 dark:text-indigo-400' };
    case 'play_event.registration_received':
      return { Icon: Trophy, iconClass: 'text-amber-600 dark:text-amber-400' };
    case 'play_event.registered':
    case 'play_event.payment_received':
      return { Icon: Trophy, iconClass: 'text-violet-600 dark:text-violet-400' };
    case 'accounting.sync.failed':
      return { Icon: Link2, iconClass: 'text-rose-600 dark:text-rose-400' };
    default:
      return { Icon: Inbox, iconClass: 'text-gray-500 dark:text-gray-400' };
  }
}

function parseCreatedAt(n: Notification): Date {
  const raw = n.createdAt as unknown;
  if (raw instanceof Date) return raw;
  if (typeof raw === 'string' || typeof raw === 'number') return new Date(raw);
  return new Date();
}

export interface RecentActivityProps {
  academyId?: string;
  clubId?: string;
  /** For tour / e2e anchors */
  cardId?: string;
}

export default function RecentActivity({ academyId, clubId, cardId }: RecentActivityProps) {
  const [items, setItems] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!academyId && !clubId) {
      setItems([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = academyId
        ? await academyService.getRecentActivity(academyId, 15)
        : await clubService.getRecentActivity(clubId!, 15);
      if (res.success && res.data) {
        setItems(res.data);
      } else {
        setItems([]);
        setError(res.error ?? 'Could not load activity');
      }
    } catch (e) {
      console.error('Recent activity load failed', e);
      setItems([]);
      setError('Could not load activity');
    } finally {
      setLoading(false);
    }
  }, [academyId, clubId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const onFocus = () => {
      void load();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [load]);

  const hasScope = Boolean(academyId || clubId);
  const emptyStateHint = academyId
    ? 'Bookings, court hires, cancellations, absences, and refund requests will appear here.'
    : 'Court hires, play event registrations, and cancellations will appear here.';

  return (
    <Card id={cardId}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Activity</h3>
        <Link
          to="/settings?tab=notifications"
          className={cn(
            'text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-md px-2 py-1 -mr-2'
          )}
        >
          View all
        </Link>
      </CardHeader>
      <CardContent>
        {!hasScope ? (
          <p className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
            Select an academy to see recent activity.
          </p>
        ) : loading ? (
          <ul className="space-y-4" aria-busy="true" aria-label="Loading recent activity">
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="flex gap-3">
                <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-full" />
                </div>
              </li>
            ))}
          </ul>
        ) : error ? (
          <p className="text-center py-8 text-sm text-red-600 dark:text-red-400">{error}</p>
        ) : items.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <div className="flex justify-center mb-2">
              <Inbox className="w-10 h-10 text-gray-400" aria-hidden />
            </div>
            <p>No recent activity yet.</p>
            <p className="text-sm mt-1">{emptyStateHint}</p>
          </div>
        ) : (
          <ul className="space-y-4 max-h-[22rem] overflow-y-auto pr-1">
            {items.map((n) => {
              const { Icon, iconClass } = activityVisuals(n.type, n.entityType);
              const when = formatDistanceToNow(parseCreatedAt(n), { addSuffix: true });
              return (
                <li key={n.id} className="flex gap-3 text-left">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-muted"
                    aria-hidden
                  >
                    <Icon className={`h-5 w-5 ${iconClass}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-white leading-snug">{n.title}</p>
                    {n.body ? (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5 leading-snug">{n.body}</p>
                    ) : null}
                    <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">{when}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
